import { useEffect, useState } from "react";
import api from "@/lib/api";
import CrudPage from "@/components/CrudPage";
import { useAuth, canWrite } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";

export default function MaterialUsage() {
  const { user } = useAuth();
  const [batches, setBatches] = useState(null);
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/batches"), api.get("/materials")]).then(([b, m]) => {
      setBatches(b.data);
      setMaterials(m.data);
    }).catch(() => setBatches([]));
  }, []);

  if (!batches) return <div className="text-slate-400 py-20 text-center">Memuat...</div>;
  const mmap = Object.fromEntries(materials.map((m) => [m.id, m]));

  return (
    <CrudPage
      title="Material Usage"
      subtitle="Pemakaian material aktual per batch. Sistem menandai jika waste melebihi standar."
      endpoint="/material-usages"
      canWrite={canWrite(user?.role, ["admin_material"])}
      scope={{ key: "batch_id", placeholder: "Pilih Batch", options: batches.map((b) => ({ value: b.id, label: `${b.no_batch} — ${b.product_name}` })) }}
      columns={[
        { key: "material_id", label: "Material", render: (r) => mmap[r.material_id]?.name || "-" },
        { key: "jumlah_standar", label: "Std", align: "right", render: (r) => num(r.jumlah_standar, 1) },
        { key: "jumlah_aktual", label: "Aktual", align: "right", render: (r) => num(r.jumlah_aktual, 1) },
        { key: "harga_aktual", label: "Harga", align: "right", render: (r) => rupiah(r.harga_aktual) },
        { key: "waste", label: "Waste %", align: "right", render: (r) => {
          const w = Math.max(r.jumlah_aktual - r.jumlah_standar, 0);
          const wp = r.jumlah_standar ? (w / r.jumlah_standar) * 100 : 0;
          const std = mmap[r.material_id]?.waste_standar_pct || 0;
          return <span className={wp > std ? "text-red-600 font-semibold inline-flex items-center gap-1" : ""}>{wp > std && <AlertTriangle className="h-3.5 w-3.5" />}{num(wp, 1)}%</span>;
        } },
        { key: "biaya", label: "Biaya Bahan", align: "right", render: (r) => rupiah(r.jumlah_aktual * r.harga_aktual) },
        { key: "biaya_waste", label: "Biaya Waste", align: "right", render: (r) => rupiah(Math.max(r.jumlah_aktual - r.jumlah_standar, 0) * r.harga_aktual) },
      ]}
      fields={[
        { key: "material_id", label: "Material", type: "select", options: materials.map((m) => ({ value: m.id, label: `${m.name} (${m.satuan})` })) },
        { key: "jumlah_standar", label: "Jumlah Standar", type: "number", step: "0.1" },
        { key: "jumlah_aktual", label: "Jumlah Aktual", type: "number", step: "0.1" },
        { key: "harga_aktual", label: "Harga Aktual (Rp)", type: "number" },
      ]}
    />
  );
}
